import {type ConnectionOperation, OPTIMISTIC_KEY} from '@github-ui/optimistic-connection'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {type QueryKey, useQueryClient} from '@github-ui/react-query'
import {updateIssueProjectsMutation as updateIssueProjectsQueryId} from '@github-ui/server-persisted-queries'
import type {UpdateIssueProjectsMutationMutation} from '@github-ui/server-persisted-queries/types/updateIssueProjectsMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, ConnectionHandler, type Disposable, type RecordSourceProxy} from 'relay-runtime'

export const PROJECT_ITEMS_CONNECTION_PATH = ['projectItems']
const PENDING_PROJECT_ITEM_ID_PREFIX = 'client:newProjectItem:'

type ProjectItem = NonNullable<
  NonNullable<NonNullable<UpdateIssueProjectsMutationMutation['addProjectV2ItemById']>['projectEdge']>['node']
>
type Project = Omit<ProjectItem['project'], 'template' | 'field'> &
  Partial<Pick<ProjectItem['project'], 'template' | 'field'>>
type AddProjectOperation = Extract<ConnectionOperation, {type: 'addToConnection'}>
type UpdateIssueProjectsCallbacks = {
  onError?: (error: Error) => void
  onCompleted?: (response: UpdateIssueProjectsMutationMutation) => void
}

export function isPendingProjectItemId(id: string): boolean {
  return id.startsWith(PENDING_PROJECT_ITEM_ID_PREFIX)
}

function addProjectOperation(node: ProjectItem): AddProjectOperation {
  return {type: 'addToConnection', connectionPath: PROJECT_ITEMS_CONNECTION_PATH, node, anchor: {at: 'end'}}
}

function projectItemFromOperation(operation: ConnectionOperation): ProjectItem {
  if (operation.type !== 'addToConnection') throw new Error('Expected a project addition')
  return operation.node as ProjectItem
}

function confirmedProjectItem(data: UpdateIssueProjectsMutationMutation): ProjectItem {
  const item = data.addProjectV2ItemById?.projectEdge?.node
  if (!item?.id) throw new Error('Failed to add issue to project')
  return item
}

async function updateIssueProjectsRequest({
  owner,
  repo,
  issueNumber,
  projectId,
  clientMutationId,
}: {
  owner: string
  repo: string
  issueNumber: number
  projectId: string
  clientMutationId?: string
}): Promise<UpdateIssueProjectsMutationMutation> {
  const query = new URLSearchParams({queryId: updateIssueProjectsQueryId})
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${issueNumber}/project_item.json?${query}`,
    {method: 'POST', body: {projectId, clientMutationId}},
  )
  const result = (await response.json()) as {
    data?: UpdateIssueProjectsMutationMutation | null
    errors?: ReadonlyArray<{message?: string}> | null
    error?: string
  } | null
  const responseError = result?.error
  const graphQLError = result?.errors?.find(item => item.message)?.message
  if (!response.ok || responseError || !result?.data) {
    throw new Error(responseError ?? graphQLError ?? 'Failed to add issue to project')
  }
  if (confirmedProjectItem(result.data).project?.id !== projectId) {
    throw new Error('Failed to add issue to project')
  }
  return result.data
}

// RELAY-CLEANUP(ProjectsSectionQuery): mirror additions for remaining Relay sidebar readers.
function appendRelayProjectItem(store: RecordSourceProxy, connectionId: string, item: ProjectItem): void {
  const connection = store.get(connectionId)
  if (!connection) return

  const node = store.get(item.id) ?? store.create(item.id, 'ProjectV2Item')
  node.setValue(item.id, 'id')
  node.setValue(item.isArchived, 'isArchived')

  const project = item.project
  const projectRecord = store.get(project.id) ?? store.create(project.id, 'ProjectV2')
  for (const key of [
    'id',
    'title',
    'closed',
    'number',
    'url',
    'template',
    'viewerCanUpdate',
    'hasReachedItemsLimit',
  ] as const) {
    projectRecord.setValue(project[key], key)
  }
  node.setLinkedRecord(projectRecord, 'project')

  const status = item.fieldValueByName
  if (status?.__typename === 'ProjectV2ItemFieldSingleSelectValue') {
    const statusRecord = store.get(status.id) ?? store.create(status.id, status.__typename)
    for (const key of ['id', 'optionId', 'name', 'nameHTML', 'color'] as const) {
      statusRecord.setValue(status[key], key)
    }
    node.setLinkedRecord(statusRecord, 'fieldValueByName', {name: 'Status'})
  } else {
    node.setValue(null, 'fieldValueByName', {name: 'Status'})
  }

  const field = project.field
  if (field?.__typename === 'ProjectV2SingleSelectField') {
    const fieldRecord = store.get(field.id) ?? store.create(field.id, field.__typename)
    for (const key of ['id', 'databaseId', 'name'] as const) {
      fieldRecord.setValue(field[key], key)
    }
    fieldRecord.setLinkedRecords(
      field.options.map(option => {
        const optionId = `${field.id}:option:${option.id}`
        const optionRecord = store.get(optionId) ?? store.create(optionId, 'ProjectV2SingleSelectFieldOption')
        for (const key of ['id', 'optionId', 'name', 'nameHTML', 'color', 'descriptionHTML', 'description'] as const) {
          optionRecord.setValue(option[key], key)
        }
        return optionRecord
      }),
      'options',
    )
    projectRecord.setLinkedRecord(fieldRecord, 'field', {name: 'Status'})
  } else {
    projectRecord.setValue(null, 'field', {name: 'Status'})
  }

  ConnectionHandler.deleteNode(connection, item.id)
  ConnectionHandler.insertEdgeAfter(
    connection,
    ConnectionHandler.createEdge(store, connection, node, 'ProjectV2ItemEdge'),
  )
}

async function notifyMutationResult(
  mutation: Promise<UpdateIssueProjectsMutationMutation>,
  callbacks?: UpdateIssueProjectsCallbacks,
): Promise<void> {
  let response: UpdateIssueProjectsMutationMutation
  try {
    response = await mutation
  } catch (error) {
    callbacks?.onError?.(error instanceof Error ? error : new Error('Failed to add issue to project'))
    return
  }
  callbacks?.onCompleted?.(response)
}

export function useUpdateIssueProjectsMutation({
  owner,
  repo,
  issueNumber,
  issueId,
  connectionId,
  settlementQueryKey,
  clientMutationId,
}: {
  owner: string
  repo: string
  issueNumber: number
  issueId: string
  connectionId?: string
  settlementQueryKey?: QueryKey
  clientMutationId?: string
}) {
  const environment = useRelayEnvironment()
  const queryClient = useQueryClient()
  const {mutateAsync} = useConnectionMutation<UpdateIssueProjectsMutationMutation, Error, Disposable | undefined>({
    ownerId: issueId,
    connectionPath: PROJECT_ITEMS_CONNECTION_PATH,
    scope: {id: `update-issue-projects:${issueId}`},
    mutationFn: operation =>
      updateIssueProjectsRequest({
        owner,
        repo,
        issueNumber,
        projectId: projectItemFromOperation(operation).project.id,
        clientMutationId,
      }),
    onMutate: ({operation}) =>
      connectionId
        ? environment.applyUpdate({
            storeUpdater: store => appendRelayProjectItem(store, connectionId, projectItemFromOperation(operation)),
          })
        : undefined,
    onSuccess: async (data, {operation: pendingOperation}, revertRelayStore) => {
      try {
        const item = confirmedProjectItem(data)
        const operation = addProjectOperation(item)
        operation.node = {...item, [OPTIMISTIC_KEY]: projectItemFromOperation(pendingOperation).id}
        if (settlementQueryKey) {
          await settleConfirmedOperation({
            queryClient,
            targets: [{queryKey: settlementQueryKey}],
            ownerId: issueId,
            operation,
          })
        }
        await notifySettlement(operation, issueId, PROJECT_ITEMS_CONNECTION_PATH)
        if (connectionId) {
          commitLocalUpdate(environment, store => appendRelayProjectItem(store, connectionId, item))
        }
      } finally {
        revertRelayStore?.dispose()
      }
    },
    onError: (_error, _variables, revertRelayStore) => revertRelayStore?.dispose(),
  })

  return useCallback(
    (project: Project, callbacks?: UpdateIssueProjectsCallbacks) => {
      const item: ProjectItem = {
        __typename: 'ProjectV2Item',
        id: `${PENDING_PROJECT_ITEM_ID_PREFIX}${project.id}`,
        isArchived: false,
        fieldValueByName: null,
        project: {...project, template: project.template ?? false, field: project.field ?? null},
      }
      void notifyMutationResult(mutateAsync({operation: addProjectOperation(item)}), callbacks)
    },
    [mutateAsync],
  )
}
